import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import DeleteModal from "../../../Components/Common/DeleteModal";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import { useNavigate } from "react-router-dom";
import CommonAddModal from "../../../Components/Common/Modal/CommonAddModal";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";

// NEW: Import the new thunks and form components
// import { 
//   fetchAllLaws, 
//   fetchLawTableColumnNames, 
//   createNewLaw, 
//   updateLaw, 
//   deleteLaw, 
//   addAmendment, 
//   addReplacement 
// } from "../../../slices/Law/thunk"; 
import { updateUserPreference } from "../../../slices/thunks";
import AddNewLawForm from "../../../Components/Common/LawStructure/AddNewLawForm";
// import AmendmentForm from "../../../Components/Common/AmendmentForm/AmendmentForm"; // NEW
// import ReplacementForm from "../../../Components/Common/ReplacementForm/ReplacementForm"; // NEW

// NEW: Configuration for the "Add/Edit Law" form
const addNewLawLabels = [
    {
            id: 1,
            labelName: 'Law Name',
            fieldName: 'select-box',
            name: 'LawID',
            required: true,
            errorMessage: 'Please select a law.',
        },
        {
            id: 2,
            labelName: 'Law No.',
            fieldName: 'text-box',
            name: 'LawNo',
            required: true,
            disabled: true, // This field is read-only
        },
        {
            id: 3,
            labelName: 'Enacted On',
            fieldName: 'date-picker',
            name: 'EnactedOn',
            required: true,
            disabled: true, // This field is read-only
        },
        {
            id: 4,
            labelName: 'Total Articles',
            fieldName: 'number-box',
            name: 'TotalArticles',
            required: true,
            disabled: true, // This field is read-only
        },
        {
            id: 5,
            labelName: 'Article No. (Select articles to include)',
            fieldName: 'text-box',
            name: 'ArticleNumbers', 
            required: true,
            errorMessage: 'Please select at least one article.',
        },
];

const actionConfig = {
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit Law",
  },
  amend: {
    icon: "ri-scales-3-line", 
    tooltip: "Amend Law",
  },
  replace: {
    icon: "ri-swap-box-line", 
    tooltip: "Replace Law",
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete",
  },
};

function LawManagement() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // NEW: Cleaned up state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [columnSelectModal, setColumnSelectModal] = useState(false);  
  const [selectedColumns, setSelectedColumns] = useState([]);

  // NEW: State for modals and the selected law item
  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [amendmentModalOpen, setAmendmentModalOpen] = useState(false);
  const [replacementModalOpen, setReplacementModalOpen] = useState(false);
  const [selectedLaw, setSelectedLaw] = useState(null); 

  // CHANGED: Selectors now point to the Law slice
  const { laws, loading, tableColumnNames } = useSelector(
    (state) => ({
      laws: state.Law.laws,
      tableColumnNames: state.Law.tableColumnNames,
      loading: state.Law.loading,
    })
  );

  // CHANGED: Corrected permission for Law Management
  const permission = usePermissions(2, 11);

  // NEW: All click handlers are now streamlined
  const onAddClickHandler = () => {
    setSelectedLaw(null); // Clear selection for 'Add' mode
    setAddEditModalOpen(true);
  };

  const onEditClickHandler = (law) => {
    setSelectedLaw(law);
    setAddEditModalOpen(true);
  };

  const onAmendClickHandler = (law) => {
    setSelectedLaw(law);
    setAmendmentModalOpen(true);
  };

  const onReplaceClickHandler = (law) => {
    setSelectedLaw(law);
    setReplacementModalOpen(true);
  }; 

  const onDeleteIconClick = (id) => {
      setSelectedIds([id]);
      setDeleteModal(true);
  }

  // NEW: Submission handlers for each modal
  const onSubmitNewLaw = (values, { resetForm }) => {
    if (selectedLaw) { // It's an Edit operation
      dispatch(updateLaw({ ...values, ID: selectedLaw.ID }));
    } else { // It's an Add operation
      dispatch(createNewLaw(values));
    }
    resetForm();
    setAddEditModalOpen(false);    
  };

  const onSubmitAmendment = (values, { resetForm }) => {
    const payload = {
        OriginalLawID: selectedLaw.ID,
        ArticlesToAmend: values.ArticlesToAmend, // Assuming multi-select gives an array
        NewLawNo: values.NewLawNo,
    };
    dispatch(addAmendment(payload));
    resetForm();
    setAmendmentModalOpen(false);
  };

  const onSubmitReplacement = (values, { resetForm }) => {
     const payload = {
        OriginalLawID: selectedLaw.ID,
        NewLawNo: values.NewLawNo,
        TotalArticlesInNewLaw: values.TotalArticlesInNewLaw,
    };
    dispatch(addReplacement(payload));
    resetForm();
    setReplacementModalOpen(false);
  };
  
const confirmDelete = () => {
dispatch(deleteLaw({ IDs: selectedIds }))
    .then(() => setSelectedIds([]));
setDeleteModal(false);
};

const saveUserPreferences = async () => {
    const preferencesPayload = {
    ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ID] = selectedColumns.includes(column.ID);
        return acc;
    }, {}),
    };

    setModalOpen(false);
    try {
    await dispatch(updateUserPreference(preferencesPayload)).unwrap();
    await dispatch(
        fetchCategoryTableColumnNames({
        module: "CATEGORY",
        })
    );

    } catch (error) {
    console.error("Error updating preferences:", error);
    }
};

  // CHANGED: Fetches Law data
  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchLawTableColumnNames({ module: "AI_LAW_STRUCTURE" }));
      await dispatch(fetchAllLaws());
    };
    fetchData();
  }, [dispatch]);
  

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("Law Structure & Status")} />
        <Card>
          <CardHeader>
            <Row className="g-4">
              <Col className="col-sm-auto">
                <div>
                  <Button
                    className="btn btn-soft-danger ms-2 me-2"
                    onClick={() => setDeleteModal(true)}
                    disabled={selectedIds.length === 0}
                  >
                    <i className="ri-delete-bin-2-line"></i>
                  </Button>
                  {permission.CanAdd && (
                    <Button
                      color="secondary"
                      className="add-btn me-1"
                      onClick={onAddClickHandler} // CHANGED
                      id="create-btn"
                    >
                      <i className="ri-add-line align-bottom me-1"></i>{t("Add New Law")}
                    </Button>
                  )}
                </div>
              </Col>
              <Col className="col-sm">
                <div className="d-flex justify-content-sm-end">
                  <Button
                    className="btn btn-soft"
                    onClick={() => setColumnSelectModal(true)} // CHANGED
                  >
                    <i className="ri-filter-3-line"></i>
                  </Button>
                  <div className="search-box ms-2 me-2">
                    <Input
                      type="text"
                      className="form-control search"
                      placeholder={t("Search Laws...")} // CHANGED
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                </div>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center">
                <Spinner style={{ height: "3rem", width: "3rem" }}>Loading...</Spinner>
              </div>
            ) : (
              <CommonDataTable
                data={laws} 
                tableColumnNames={tableColumnNames}
                searchTerm={searchTerm}
                permission={permission}
                actionConfig={actionConfig} 
                onEditClickHandler={onEditClickHandler}
                onAmendClickHandler={onAmendClickHandler}
                onReplaceClickHandler={onReplaceClickHandler}
                onDeleteIconClick={onDeleteIconClick} 
                setSelectedIds={setSelectedIds}
              />
            )}
          </CardBody>
        </Card>
      </Container>

      {/* NEW: All modals are now configured for Laws */}
      <CommonAddModal
          show={addEditModalOpen}
          setShow={setAddEditModalOpen}
          title={selectedLaw ? t("Edit Law") : t("Add New Law")}
          FormBody={AddNewLawForm}
          labels={addNewLawLabels} // Pass the labels for the form
          selectedItemData={selectedLaw}
          onSubmitHandler={onSubmitNewLaw}
          size="lg"
      />
      {/* <CommonAddModal
          show={amendmentModalOpen}
          setShow={setAmendmentModalOpen}
          title={`${t("Amend Law")}: ${selectedLaw?.LawName || ''}`}
          FormBody={AmendmentForm}
          FormBodyProps={{ originalLaw: selectedLaw }}
          onSubmitHandler={onSubmitAmendment}
      />
      <CommonAddModal
          show={replacementModalOpen}
          setShow={setReplacementModalOpen}
          title={`${t("Replace Law")}: ${selectedLaw?.LawName || ''}`}
          FormBody={ReplacementForm}
          FormBodyProps={{ originalLaw: selectedLaw }}
          onSubmitHandler={onSubmitReplacement}
      /> */}

      <ColumnSelectionModal
          isOpen={columnSelectModal} // CHANGED
          toggle={() => setColumnSelectModal(!columnSelectModal)}
          columns={tableColumnNames}
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
          handleSave={saveUserPreferences}
      />

      <DeleteModal
          showDelete={deleteModal}
          setShowDelete={setDeleteModal}
          onDeleteClick={confirmDelete}
          description={t("confirmDeleteLaw")}
      />
    </div>
  );
}

export default LawManagement;